/**
 * #1006 — Polymarket US in the Tracker's "Cancel all".
 *
 * Brendan (Sep 29): US belongs in Cancel all, as per-order cancels through the
 * real US client, gated on each account's cancel permission and readiness,
 * with each order's own result and "unconfirmed" kept apart from failed.
 *
 * Scope is the caller's OWN orders in every visible US account. That is the
 * international precedent for traders and managers (the relay refuses a
 * trader's account-wide cancel so a sweep never takes teammates' orders,
 * relay/src/routes/polymarket-proxy.ts). The admin desk-wide list is an
 * inherited policy still logged as undecided (docs/polymarket-first-class.md
 * §7), and US decouples desk cancellation from role altogether (explicit
 * cancelDesk grant, docs/260927PolymarketUsCancellation.md), so desk orders
 * are left out and the dialog says so.
 *
 * Unlike the other venues, US orders are not held in memory: they are read
 * per account when the confirmation opens and frozen there, so later reads
 * cannot add orders. Uses its own client instances; the US section's visit is
 * never disturbed. Nothing here polls or retries.
 */
import type { UsAccountClient, UsAccountContext } from '../../lib/polymarketUs/accountClient';
import { usCancelErrorOutcome, usCancelResultOutcome, type UsCancelOutcome } from '../../lib/polymarketUs/cancelOutcome';
import type { UsPortfolioClient } from '../../lib/polymarketUs/portfolioClient';
import { presentUsOrder, usCancelView } from '../../lib/polymarketUs/portfolioModel';
import type { UsOrderRow, UsPositionRow, UsReadiness, UsScope } from '../../lib/polymarketUs/types';
// Relative imports, as in lib/polymarketUs: the browser-relay integration
// suite imports this module under its own compiler, without the `@/` alias.

export type UsBulkClients = {
  accounts: Pick<UsAccountClient, 'listAccounts' | 'selectAccount'>;
  portfolio: Pick<UsPortfolioClient, 'setSelection' | 'readOrderPage' | 'cancelOrder'>;
};

export type UsBulkTarget = {
  key: string;
  accountId: string;
  scope: UsScope;
  intentId: string;
  orderId: string;
  label: string;
};

/** Open own orders a sweep will not send, grouped by account and reason. A
 * null count means the account could not be read at all. */
export type UsBulkExclusion = { accountId: string; orders: number | null; reason: string };

export type UsBulkInventory = {
  readonly targets: readonly UsBulkTarget[];
  readonly excluded: readonly UsBulkExclusion[];
  /** Accounts that contributed at least one target. */
  readonly accounts: number;
};

export type UsBulkResult = { target: UsBulkTarget; outcome: UsCancelOutcome | 'not-sent'; code: string | null };

/** Bounds on one confirmation's reads; reaching one is disclosed, never silent. */
export const US_BULK_MAX_ACCOUNT_PAGES = 20;
export const US_BULK_MAX_ORDER_PAGES = 50;

const code = (error: unknown) => error && typeof error === 'object' && 'code' in error
  && typeof error.code === 'string' ? error.code : 'US_READ_UNAVAILABLE';

export function readinessOf(context: UsAccountContext): UsReadiness {
  const r = context.readiness;
  return r.state === 'ready'
    ? { state: 'ready', canSubmit: r.canSubmit, canCancelOwn: r.canCancelOwn, canCancelDesk: r.canCancelDesk }
    : { state: 'unavailable', reason: r.reason };
}

/** Why an open own order is not cancellable right now, in dialog wording.
 * Exported for #1016's per-row wording in Game Detail. */
export function exclusionReason(blocked: string | null, status: string | null): string {
  if (blocked?.startsWith('Account unavailable')) return 'account not ready';
  if (blocked === 'You do not have permission to cancel your orders') return 'no cancel permission on this account';
  if (blocked) return blocked.charAt(0).toLowerCase() + blocked.slice(1);
  if (status) return `a cancel is already under way (${status.toLowerCase()})`;
  return 'not cancellable here';
}

/** One visible account's own open orders as read, before they are reduced to
 * targets (#1016: Game Detail matches them to a game first). */
export type UsOwnOrdersRead =
  | { accountId: string; ok: true; scope: UsScope; readiness: UsReadiness;
      /** Own, non-terminal, one row per intent, in read order. */
      orders: readonly UsOrderRow[];
      /** The saved-order pages ran past the bound; the rest were not read. */
      truncated: boolean;
      /** #1233 — the holdings the first order page carries, except ones known
       * to be zero (Game Detail's held snapshot; a sweep ignores them). */
      positions: readonly UsPositionRow[];
      /** Why the holdings could not be read; null when they were. */
      positionsError: string | null }
  | { accountId: string; ok: false; code: string };

/** "0", "-0.000": a holding known to be empty. Unknown (null) is not zero. */
export function isKnownZero(quantity: string | null): boolean {
  return quantity !== null && /^-?0+(?:\.0+)?$/.test(quantity);
}

export const US_BULK_TRUNCATED_REASON = 'more saved orders than one sweep reads; the rest were not read';

/**
 * Read every visible account's own open orders. `stillCurrent` is checked
 * between reads; a login or org change aborts the whole read (the caller shows
 * nothing from it). One account's failure is that account's result only.
 */
export async function readUsOwnOpenOrders(
  clients: UsBulkClients,
  stillCurrent: () => boolean,
): Promise<UsOwnOrdersRead[]> {
  const guard = () => { if (!stillCurrent()) throw Object.assign(new Error('US_CLIENT_CONTEXT_CHANGED'), { code: 'US_CLIENT_CONTEXT_CHANGED' }); };
  const accountIds: string[] = [];
  let after: string | undefined;
  for (let page = 0; ; page++) {
    const result = await clients.accounts.listAccounts(after);
    guard();
    for (const a of result.accounts) if (a.canViewOwn) accountIds.push(a.accountId);
    if (!result.nextCursor) break;
    if (page + 1 >= US_BULK_MAX_ACCOUNT_PAGES) throw Object.assign(new Error('US_BULK_TOO_MANY_ACCOUNTS'), { code: 'US_BULK_TOO_MANY_ACCOUNTS' });
    after = result.nextCursor;
  }

  const reads: UsOwnOrdersRead[] = [];
  try {
    for (const accountId of accountIds) {
      try {
        const context = await clients.accounts.selectAccount(accountId, 'own');
        guard();
        clients.portfolio.setSelection({ accountId: context.accountId, ...context.scope, view: 'own' });
        const orders: UsOrderRow[] = [];
        let positions: readonly UsPositionRow[] = [];
        let positionsError: string | null = null;
        const seen = new Set<string>();
        let truncated = false;
        let cursor: string | undefined;
        for (let page = 0; ; page++) {
          const snapshot = await clients.portfolio.readOrderPage(context.scope, cursor);
          guard();
          if (page === 0) {
            // Every page carries the same holdings read; the first one is used.
            const inventory = snapshot.readDetails?.inventory;
            positionsError = inventory?.state === 'unavailable' ? inventory.reason : null;
            positions = snapshot.positions.filter((p) => !isKnownZero(p.heldQuantity));
          }
          for (const row of snapshot.orders) {
            // A row repeated across saved pages is one order, considered once.
            if (row.ownership !== 'own' || presentUsOrder(row).terminal || seen.has(row.intentId)) continue;
            seen.add(row.intentId);
            orders.push(row);
          }
          const next = snapshot.readDetails?.nextOrderCursor ?? null;
          if (!next) break;
          if (page + 1 >= US_BULK_MAX_ORDER_PAGES) {
            truncated = true;
            break;
          }
          cursor = next;
        }
        reads.push({ accountId, ok: true, scope: context.scope, readiness: readinessOf(context), orders, truncated,
          positions, positionsError });
      } catch (error) {
        if (code(error) === 'US_CLIENT_CONTEXT_CHANGED' && !stillCurrent()) throw error;
        reads.push({ accountId, ok: false, code: code(error) });
      }
    }
  } finally {
    clients.portfolio.setSelection(null);
  }
  return reads;
}

/**
 * Read every visible account's own open orders and split them into targets
 * and disclosed exclusions. `stillCurrent` is checked between reads; a login
 * or org change aborts the whole read (the caller shows nothing from it).
 */
export async function readUsBulkInventory(
  clients: UsBulkClients,
  nowMs: number,
  stillCurrent: () => boolean,
): Promise<UsBulkInventory> {
  const reads = await readUsOwnOpenOrders(clients, stillCurrent);
  const targets: UsBulkTarget[] = [];
  const excluded: UsBulkExclusion[] = [];
  let accounts = 0;
  for (const read of reads) {
    if (!read.ok) {
      excluded.push({ accountId: read.accountId, orders: null, reason: `orders could not be read (${read.code})` });
      continue;
    }
    const { inventory } = usVisibleInventory({ ...read, pending: new Set() }, nowMs);
    targets.push(...inventory.targets);
    excluded.push(...inventory.excluded);
    if (read.truncated) excluded.push({ accountId: read.accountId, orders: null, reason: US_BULK_TRUNCATED_REASON });
    accounts += inventory.accounts;
  }
  return { targets, excluded, accounts };
}

/**
 * Cancel visible (#1006, Brendan's review): exactly the US orders the section
 * shows under the chips — the selected account only — frozen from what is on
 * screen, with no new read. Own orders only, as in Cancel all; a teammate's
 * order shown in the desk view is named as not included. `unclassified`
 * counts targets shown only because their market is not classified yet.
 */
export function usVisibleInventory(
  shown: { accountId: string; scope: UsScope; readiness: UsReadiness; orders: readonly UsOrderRow[]; pending: ReadonlySet<string> },
  nowMs: number,
): { inventory: UsBulkInventory; unclassified: number } {
  const targets: UsBulkTarget[] = [];
  const reasons = new Map<string, number>();
  let unclassified = 0;
  for (const row of shown.orders) {
    if (presentUsOrder(row).terminal) continue;
    if (row.ownership !== 'own') {
      reasons.set('teammates’ orders on this shared account', (reasons.get('teammates’ orders on this shared account') ?? 0) + 1);
      continue;
    }
    const view = usCancelView(row, shown.readiness, nowMs);
    // Bulk cancellation is retail-only; a native order is cancelled singly.
    if (view.canCancel && row.orderId && row.protocol !== 'institutional') {
      targets.push({ key: `${shown.accountId}:${row.intentId}`, accountId: shown.accountId, scope: shown.scope,
        intentId: row.intentId, orderId: row.orderId, label: row.target.label });
      if (shown.pending.has(row.intentId)) unclassified++;
    } else {
      const reason = exclusionReason(view.blockedReason, view.status);
      reasons.set(reason, (reasons.get(reason) ?? 0) + 1);
    }
  }
  const excluded = [...reasons].map(([reason, orders]) => ({ accountId: shown.accountId, orders, reason }));
  return { inventory: { targets, excluded, accounts: targets.length ? 1 : 0 }, unclassified };
}

/** Cancel visible over the pooled US section: every own-view account's rows
 * on screen, one frozen inventory (targets in account order, exclusions per
 * account, `accounts` = how many hold a target). */
export function usVisibleInventoryAll(
  shown: readonly Parameters<typeof usVisibleInventory>[0][],
  nowMs: number,
): { inventory: UsBulkInventory; unclassified: number } {
  const targets: UsBulkTarget[] = [];
  const excluded: UsBulkInventory['excluded'][number][] = [];
  let unclassified = 0;
  let accounts = 0;
  for (const one of shown) {
    const part = usVisibleInventory(one, nowMs);
    targets.push(...part.inventory.targets);
    excluded.push(...part.inventory.excluded);
    accounts += part.inventory.accounts;
    unclassified += part.unclassified;
  }
  return { inventory: { targets, excluded, accounts }, unclassified };
}

/**
 * Cancel each frozen target once, one at a time, in account order. The
 * account's own-view selection is set before its first order. A login or org
 * change stops the sweep: remaining orders are reported as not sent.
 */
export async function runUsBulkCancel(
  clients: Pick<UsBulkClients, 'portfolio'>,
  targets: readonly UsBulkTarget[],
  stillCurrent: () => boolean,
): Promise<UsBulkResult[]> {
  const results: UsBulkResult[] = [];
  let selected: string | null = null;
  try {
    for (const target of targets) {
      if (!stillCurrent()) {
        results.push({ target, outcome: 'not-sent', code: 'US_CLIENT_CONTEXT_CHANGED' });
        continue;
      }
      try {
        if (selected !== target.accountId) {
          clients.portfolio.setSelection({ accountId: target.accountId, ...target.scope, view: 'own' });
          selected = target.accountId;
        }
        const result = await clients.portfolio.cancelOrder({ scope: target.scope, intentId: target.intentId, orderId: target.orderId });
        const outcome = usCancelResultOutcome(result);
        results.push({ target, outcome, code: outcome === 'requested' ? null : result.attempt.state });
      } catch (error) {
        results.push({ target, outcome: usCancelErrorOutcome(error), code: code(error) });
      }
    }
  } finally {
    clients.portfolio.setSelection(null);
  }
  return results;
}

/** "Polymarket US: 3 orders not included, account not ready" — one line per
 * account and reason, in the order they were read. */
export function describeUsExclusions(excluded: readonly UsBulkExclusion[]): string[] {
  return excluded.map(({ accountId, orders, reason }) => {
    const count = orders === null ? 'orders' : `${orders} order${orders === 1 ? '' : 's'}`;
    return `Polymarket US: ${count} not included, ${reason} (account …${accountId.slice(-4)})`;
  });
}
